import { Component, Inject, ErrorHandler, ViewChild, HostListener, OnDestroy, OnInit, signal, ChangeDetectionStrategy, Injector, afterNextRender, inject } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { Kirjanpitotili, AlvMaaritys, PaatilikartanProfiilinOsa, PaatilikartanProfiili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'

import { firstValueFrom, Subject } from 'rxjs'
import { TiliLomake } from './kirjanpitotili-muokkaa.dialog'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'

export interface KirjanpitotiliProfiiliMuokkaaDialogData {
  profiili: PaatilikartanProfiili
  profiilinOsa: PaatilikartanProfiilinOsa
  tili: Kirjanpitotili
}

interface ProfiiliLomake {
  piilotettu: boolean
  nimiFi: string
  nimiEn: string
}

@Component({
  templateUrl: './profiili-kirjanpitotili-muokkaa.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitotiliProfiiliMuokkaaDialog implements OnInit, OnDestroy {
  @ViewChild('nimiFiInput', { read: MatInput, static: true }) nimiFiInput: MatInput

  commonErrorSignal = signal<string>('')
  tiliSignal = signal<Kirjanpitotili>(null)
  form: FieldTree<TiliLomake>
  profiiliLomake: FieldTree<ProfiiliLomake>
  name = '' + Math.random()

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  vertaaAlvMaarityksia: (a: AlvMaaritys, b: AlvMaaritys) => boolean = (a: AlvMaaritys, b: AlvMaaritys): boolean => {
    return a && b && a.tunniste === b.tunniste
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: KirjanpitotiliProfiiliMuokkaaDialogData,
    private _dialogRef: MatDialogRef<KirjanpitotiliProfiiliMuokkaaDialog>,
    private _errorHandler: ErrorHandler,
    private _tilikarttaService: TilikarttaService,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  ngOnInit() {
    this.tiliSignal.set(this._data.tili)

    this.profiiliLomake = form(
      signal<ProfiiliLomake>({
        nimiFi: this._data.profiilinOsa?.nimi?.fi ?? '',
        nimiEn: this._data.profiilinOsa?.nimi?.en ?? '',
        piilotettu: !!this._data.profiilinOsa?.piilota,
      }),
      { injector: this._injector },
    )

    afterNextRender(() => this.nimiFiInput.focus(), { injector: this._injector })
  }

  get profiiliNimiFi(): FieldTree<string> {
    return this.profiiliLomake.nimiFi
  }

  get profiiliNimiEn(): FieldTree<string> {
    return this.profiiliLomake.nimiEn
  }

  @HostListener('document:keydown.esc')
  peruuta() {
    this._dialogRef.close()
  }

  @HostListener('document:keydown.enter')
  async tallenna() {
    if (this.profiiliLomake().invalid() || !this.profiiliLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.form)
      return
    }

    this._ladataanService.aloitaLataaminen()

    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)

    try {
      const piilota = !!this.profiiliLomake.piilotettu().value()
      const nimiFi = this._annaNimi(this.profiiliNimiFi, this._data.profiilinOsa?.nimi?.fi)
      const nimiEn = this._annaNimi(this.profiiliNimiEn, this._data.profiilinOsa?.nimi?.en)

      const osa: PaatilikartanProfiilinOsa = {}
      if (nimiFi || nimiEn) {
        osa.nimi = {
          fi: nimiFi ?? null,
          en: nimiEn ?? null,
        }
      }
      if (piilota) {
        osa.piilota = 1
      }

      // null poistaa osan
      const tallennettava: PaatilikartanProfiilinOsa = Object.keys(osa).length > 0 ? osa : null
      await this._tilikarttaService.tallennaPaatilikartanProfiilinOsa(this._data.profiili, this._data.tili, tallennettava, kirjanpitajanTiedot.uid)
    } catch (error) {
      this.commonErrorSignal.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.')
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._dialogRef.close()
    }
  }

  // Reaktiivisessa lomakkeessa puuttuva nimi oli null, kunnes kenttään kirjoitettiin, eikä sitä tallennettu tyhjänä merkkijonona
  private _annaNimi(kentta: FieldTree<string>, alkuperainen: string | null | undefined): string | undefined {
    if (alkuperainen == null && !kentta().dirty()) {
      return undefined
    }
    return kentta().value().trim()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
